import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { Badge, Button, Callout, Input } from "@diametral/design-system/react";

import { useProject, useProjects } from "../api/projects";
import { useReportSearch, useReports } from "../api/reports";
import type { PickerPerson } from "../api/types";
import { Page } from "../components/Page";
import { PersonSelect } from "../components/PersonPicker";
import { ReportsByWeek } from "../components/Reports";
import { SelectField } from "../components/SelectField";
import { EmptyBlock, ErrorBlock, LoadingBlock } from "../components/States";
import { WeekSelect } from "../components/WeekSelect";
import { useTranslation } from "../lib/i18n";
import { useDebounced } from "../lib/useDebounced";
import { compareWeeks, isIsoWeek } from "../lib/week";

const PAGE = 100;
const MAX = 500;

export function TeamReportsPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const weekFrom = isIsoWeek(params.get("from")) ? (params.get("from") as string) : "";
  const weekTo = isIsoWeek(params.get("to")) ? (params.get("to") as string) : "";
  const projectSlug = params.get("project") ?? "";
  const streamId = params.get("stream") ?? "";
  const [person, setPerson] = useState<PickerPerson | null>(null);
  const [search, setSearch] = useState(params.get("q") ?? "");
  const q = useDebounced(search.trim(), 400);
  const [limit, setLimit] = useState(PAGE);

  function update(changes: Record<string, string>) {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const [key, value] of Object.entries(changes)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        return next;
      },
      { replace: true },
    );
    setLimit(PAGE);
  }

  useEffect(() => {
    if ((params.get("q") ?? "") !== q) update({ q });
  }, [q]);

  const projects = useProjects({ status: "all" });
  const project = useProject(projectSlug || undefined);
  const range = weekFrom && weekTo && compareWeeks(weekFrom, weekTo) > 0;

  const filters = {
    project: projectSlug || undefined,
    person_id: person?.id,
    week_from: weekFrom || undefined,
    week_to: weekTo || undefined,
  };
  const searching = q !== "";
  const list = useReports({ ...filters, stream_id: streamId ? Number(streamId) : undefined, limit }, !searching && !range);
  const found = useReportSearch({ q, ...filters }, searching && !range);
  const results = searching ? found : list;

  const projectOptions = useMemo(
    () => [
      { value: "", label: t("All projects") },
      ...(projects.data ?? [])
        .slice()
        .sort((a, b) => a.title.localeCompare(b.title, "fr"))
        .map((p) => ({
          value: p.slug,
          label:
            p.status === "active"
              ? p.title
              : p.status === "closed"
                ? t("{{title}} (closed)", { title: p.title })
                : t("{{title}} (paused)", { title: p.title }),
        })),
    ],
    [projects.data, t],
  );
  const streamOptions = [
    { value: "", label: t("All streams") },
    ...(project.data?.streams ?? []).map((s) => ({
      value: String(s.id),
      label: s.archived ? t("{{title}} (archived)", { title: s.title }) : s.title,
    })),
  ];
  const count = results.data?.length ?? 0;
  const anyFilter = Boolean(weekFrom || weekTo || projectSlug || streamId || person || q);

  return (
    <Page
      kicker={t("Activity reports")}
      title={t("Team reports")}
      description={t(
        "All submitted reports, by week. Search their text or filter by period, project, stream and person.",
      )}
    >
      <div className="ds-block-filter-bar-01 ada-filters" role="search" aria-label={t("Filter reports")}>
        <Input
          className="ada-filters__search"
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("Search reports (full text)…")}
          aria-label={t("Search reports")}
        />
        <WeekSelect
          label={t("From week")}
          value={weekFrom}
          emptyLabel={t("From the start")}
          onChange={(v) => update({ from: v })}
        />
        <WeekSelect label={t("Up to week")} value={weekTo} emptyLabel={t("Up to today")} onChange={(v) => update({ to: v })} />
        <SelectField
          label={t("Project")}
          value={projectSlug}
          onChange={(v) => update({ project: v, stream: "" })}
          options={projectOptions}
        />
        <SelectField
          label={t("Stream")}
          value={streamId}
          onChange={(v) => update({ stream: v })}
          options={streamOptions}
          disabled={!projectSlug || searching}
        />
        <div className="ada-filters__field" style={{ minWidth: "14rem" }}>
          <PersonSelect
            label={t("Person")}
            placeholder={t("All people")}
            value={person}
            onChange={(p) => {
              setPerson(p);
              setLimit(PAGE);
            }}
          />
        </div>
        {anyFilter ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setSearch("");
              setPerson(null);
              setParams(new URLSearchParams(), { replace: true });
              setLimit(PAGE);
            }}
          >
            {t("Clear")}
          </Button>
        ) : null}
      </div>

      {range ? <Callout type="warning">{t("The start week is after the end week.")}</Callout> : null}
      {searching && streamId ? (
        <Callout type="info">{t("Full-text search does not filter by stream: the stream filter is ignored.")}</Callout>
      ) : null}

      {range ? null : results.isPending ? (
        <LoadingBlock lines={6} label={t("Loading reports…")} />
      ) : results.isError ? (
        <ErrorBlock error={results.error} onRetry={() => void results.refetch()} />
      ) : count === 0 ? (
        <EmptyBlock
          icon="file"
          title={searching ? t("No results") : t("No reports")}
          description={anyFilter ? t("Nothing matches these criteria.") : t("Submitted reports will appear here.")}
        />
      ) : (
        <div className="ada-stack">
          <div className="ada-cluster">
            <Badge variant="secondary">
              {searching ? t("{{count}} results", { count }) : t("{{count}} activity reports", { count })}
            </Badge>
            {searching ? <span className="ada-faint">{t("Ranked by relevance, grouped by week.")}</span> : null}
          </div>
          <ReportsByWeek reports={results.data ?? []} showProject />
          {!searching && count >= limit && limit < MAX ? (
            <div>
              <Button variant="outline" loading={list.isFetching} onClick={() => setLimit((l) => Math.min(l + PAGE, MAX))}>
                {t("Show more")}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Page>
  );
}
